// PnLTimeSeriesDx.tsx
import React, { useMemo, useEffect, useState, useRef } from "react";
import DataGrid, { Column, HeaderFilter, Paging, Sorting } from "devextreme-react/data-grid";
import { baseUrl } from "../../hooks/useFetch";

interface VarBacktestTableProps {
  page?: string;
  cobDate: Date | null;
  onChange?: (chartData: any[]) => void;
  book?: string;
}

interface RowData {
  __key: string;
  row_label: string;
  [key: string]: any;
}

interface BacktestResponse {
  cols: string[];
  data: Record<string, any>[];
  row_labels: string[];
  chart_data: any[];
}

interface DxColumnDef {
  dataField: string;
  caption: string;
  allowSorting?: boolean;
  allowFiltering?: boolean;
  width?: number;
}

const VarBacktestTable: React.FC<VarBacktestTableProps> = ({ page, cobDate, onChange, book }) => {
  const [rowLabels, setRowLabels] = useState<string[]>([]);
  const [cols, setCols] = useState<string[]>([]);
  const [data, setData] = useState<RowData[]>([]);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const labelDescriptionsBase = useMemo<Record<string, string>>(
    () => ({
      N: "Total number of observations (N)",
      x: "Number of exceptions (x)",
      p_hat: "Observed exception probability p\u0302 = x / N",
      alpha: "Expected exception probability \u03B1",
      ll0: "Log-likelihood under H\u2080 (model failure rate = p)",
      ll1: "Log-likelihood under H\u2081 (model failure rate = p\u0302)",
      LR_POF: "Kupiec POF test statistic LR_POF = -2 (LL\u2080 \u2212 LL\u2081)",
      p_value: "p-value (\u03C7\u00B2, df=1)",
      kupiec_POF: "Kupiec POF test decision",
      LR_IND: "Christoffersen Independence test statistic LR_IND = -2 (LL\u2080 \u2212 LL\u2081)",
      p_value_ind: "p-value (\u03C7\u00B2, df=1)",
      christoffersen_ind: "Christoffersen Independence test decision",
      LR_CC: "Joint Conditional Coverage statistic LR_CC = LR_POF + LR_IND",
      p_value_cc: "p-value (\u03C7\u00B2, df=2)",
      joint_cc: "Joint Conditional Coverage Test decision",
    }),
    []
  );

  const hiddenKeysDashboard = useMemo(() => new Set(["ll0", "ll1"]), []);

  const zeroDpKeys = useMemo(() => new Set(["p_hat", "alpha"]), []);
  const twoDpKeys = useMemo(
    () =>
      new Set([
        "ll0",
        "ll1",
        "LR_POF",
        "p_value",
        "LR_IND",
        "p_value_ind",
        "LR_CC",
        "p_value_cc",
      ]),
    []
  );

  const dxColumns = useMemo<DxColumnDef[]>(() => {
    if (!cols) return [];

    const valueColumns: DxColumnDef[] = cols.map((c) => ({
      dataField: c,
      caption: c,
    }));

    return [
      {
        dataField: "row_label",
        caption: "Metric",
        allowSorting: false,
        allowFiltering: false,
        width: 300,
      },
      ...valueColumns,
    ];
  }, [cols]);

  function onCellPrepared(e: any) {
    if (
      e.column &&
      e.column.dataField === "row_label" &&
      (e.rowType === "data" || e.rowType === "header")
    ) {
      e.cellElement.style.textAlign = "center";
      e.cellElement.style.justifyContent = "center";
      e.cellElement.style.borderRight = "2px solid  #4e4e4e";
    }
  }

  const onRowPrepared = (e: any) => {
    const rowEl = e.rowElement instanceof HTMLElement ? e.rowElement : e.rowElement?.[0];
    if (!rowEl) return;

    if (e.rowType === "data") {
      rowEl.style.height = "18px";
      rowEl.style.lineHeight = "14px";
      rowEl.querySelectorAll("td").forEach((td: HTMLElement) => {
        td.style.paddingTop = "1px";
        td.style.paddingBottom = "1px";
        td.style.paddingLeft = "4px";
        td.style.paddingRight = "4px";
        td.style.fontSize = "12px";
      });
    }

    if (e.rowType === "header") {
      rowEl.style.height = "28px";
      rowEl.style.lineHeight = "24px";
      rowEl.querySelectorAll("td").forEach((td: HTMLElement) => {
        td.style.paddingTop = "10px";
        td.style.paddingBottom = "10px";
        td.style.paddingLeft = "4px";
        td.style.paddingRight = "4px";
        td.style.fontSize = "12px";
      });
    }
  };

  const cobDateStr = useMemo(() => {
    if (!cobDate) return null;
    const d = cobDate instanceof Date ? cobDate : new Date(cobDate);
    return d.toISOString().slice(0, 10);
  }, [cobDate instanceof Date ? cobDate.getTime() : cobDate]);

  const lastFetchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      try {
        if (!cobDateStr) return;

        const bookStr = book ?? "";
        const pageStr = page ?? "";
        const fetchKey = `${cobDateStr}__${bookStr}__${pageStr}`;

        if (lastFetchKeyRef.current === fetchKey) return;
        lastFetchKeyRef.current = fetchKey;

        const params = new URLSearchParams({ cob_date: cobDateStr });
        if (bookStr !== "") params.append("book", bookStr);

        const res = await fetch(`${baseUrl}/marketrisk_vartesting?${params.toString()}`, {
          signal: ctrl.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const res_json: BacktestResponse = await res.json();

        const { cols: colsResp, data: dataResp, row_labels, chart_data } = res_json;

        const rows: RowData[] = (dataResp ?? [])
          .map((row, i) => {
            const key = row_labels?.[i];
            return {
              __key: key,
              row_label: labelDescriptionsBase[key] ?? key,
              ...row,
            };
          })
          .filter((r) => !(page === "dashboard" && hiddenKeysDashboard.has(r.__key)));

        setRowLabels(row_labels ?? []);
        setCols(colsResp ?? []);
        setData(rows);

        onChangeRef.current?.(chart_data);
      } catch (err: any) {
        if (err.name !== "AbortError") console.error("Failed to load VaR backtest data", err);
      }
    })();

    return () => ctrl.abort();
  }, [cobDateStr, book, page, hiddenKeysDashboard, labelDescriptionsBase]);

  return (
    <DataGrid
      style={{ width: "95%", margin: "0 auto" }}
      className="compact-grid"
      dataSource={data ?? []}
      showBorders
      rowAlternationEnabled
      allowColumnResizing
      allowColumnReordering
      columnAutoWidth={false}
      wordWrapEnabled
      onCellPrepared={onCellPrepared}
      onRowPrepared={onRowPrepared}
    >
      <Sorting mode="multiple" />
      <HeaderFilter visible />
      <Paging enabled={false} />

      {dxColumns.map((col) =>
        col.dataField === "row_label" ? (
          <Column key={col.dataField} {...col} />
        ) : (
          <Column
            key={col.dataField}
            {...col}
            cellRender={({ value, data }: { value: any; data: RowData }) => {
              if (value == null || value === "" || Number.isNaN(Number(value))) {
                return <span>{value ?? ""}</span>;
              }

              const key = data?.__key;
              const n = Number(value);

              if (zeroDpKeys.has(key)) {
                const rounded = Math.round(n*100);
                const text = rounded < 0 ? `(${Math.abs(rounded)})` : String(rounded);
                return <span style={rounded < 0 ? { color: "red" } : {}}>{text}%</span>;
              }

              if (twoDpKeys.has(key)) {
                const rounded = Math.round(n * 100) / 100;
                const absText = Math.abs(rounded).toFixed(2);
                return rounded < 0 ? (
                  <span style={{ color: "red" }}>({absText})</span>
                ) : (
                  <span>{absText}</span>
                );
              }

              if (n < 0) {
                const text = `(${String(value).replace(/^-/, "")})`;
                return <span style={{ color: "red" }}>{text}</span>;
              }

              return <span>{String(value)}</span>;
            }}
          />
        )
      )}
    </DataGrid>
  );
};

export default VarBacktestTable;
